import React, { ReactElement, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import GenericChip from "components/shared/status/GenericChip";

import { getBaremetalFirewall } from "redux/selectors/baremetalSelector";

import CreateFirewallConfiguration from "./actions/CreateFirewallConfiguration";
import DeleteFirewallConfiguration from "./actions/DeleteFirewallConfiguration";
import EditFirewallConfiguration from "./actions/EditFirewallConfiguration";
import ShowRule from "./components/ShowRule";

const BaremetalFirewall = (): ReactElement => {
 const params = useParams<{ id: string; section: string }>();

 const firewall = useSelector(getBaremetalFirewall);

 const [editFirewall, setEditFirewall] = useState<boolean>(false);
 const [createFirewall, setCreateFirewall] = useState<boolean>(false);

 const handleStartEdit = () => setEditFirewall(true);
 const handleEndEdit = () => setEditFirewall(false);

 const handleCreate = () => setCreateFirewall(true);
 const handleStopCreate = () => setCreateFirewall(false);

 return createFirewall ? (
  <CreateFirewallConfiguration barmetal_id_db={Number(params.id)} closeEdit={handleStopCreate} />
 ) : !firewall ? (
  <Stack spacing={2}>
   <Alert severity="info">
    <FormattedMessage id="baremetal.firewall.noFirewallConfiguration" />
   </Alert>
   <Stack direction="row" justifyContent="flex-end">
    <Button onClick={handleCreate}>
     <FormattedMessage id="baremetal.firewall.createFirewall" />
    </Button>
   </Stack>
  </Stack>
 ) : editFirewall ? (
  <EditFirewallConfiguration firewall={firewall} closeEdit={handleEndEdit} />
 ) : (
  <Stack spacing={2}>
   <Stack>
    <Stack direction="row" spacing={1} alignItems="center">
     <Typography variant="caption" fontWeight="bold">
      <FormattedMessage id="baremetal.firewall.status" />:
     </Typography>
     <GenericChip
      text={firewall?.status === "active" ? "Active" : "Disabled"}
      type={firewall?.status === "active" ? "success" : "error"}
     />
    </Stack>
    <Stack direction="row" spacing={1} alignItems="center">
     <Typography variant="caption" fontWeight="bold">
      <FormattedMessage id="baremetal.firewall.whitelistHos" />:
     </Typography>
     <GenericChip
      text={firewall?.whitelist_hos ? "Active" : "Disabled"}
      type={firewall?.whitelist_hos ? "success" : "error"}
     />
    </Stack>
   </Stack>
   <Stack>
    <Stack textAlign="left">
     <Typography variant="unboundedTitle" fontWeight={16}>
      <FormattedMessage id="baremetal.firewall.inputRules" />
     </Typography>
    </Stack>
    <Divider />
    <Stack spacing={1} p={2} sx={{ overflowX: "auto" }}>
     {firewall && firewall?.input_rules?.length > 0 ? (
      firewall?.input_rules?.map((element, index) => {
       return <ShowRule rule={element} key={`input-rule-${index}`} />;
      })
     ) : (
      <Stack>
       <Typography>
        <FormattedMessage id="baremetal.firewal.noRuleFound" />
       </Typography>
      </Stack>
     )}
    </Stack>
   </Stack>
   <Stack>
    <Stack textAlign="left">
     <Typography variant="unboundedTitle" fontWeight={16}>
      <FormattedMessage id="baremetal.firewall.outputRules" />
     </Typography>
    </Stack>
    <Divider />
    <Stack spacing={1} p={2} sx={{ overflowX: "auto" }}>
     {firewall && firewall?.output_rules.length > 0 ? (
      firewall?.output_rules.map((element, index) => {
       return <ShowRule rule={element} key={`output-rule-${index}`} />;
      })
     ) : (
      <Stack>
       <Typography>
        <FormattedMessage id="baremetal.firewal.noRuleFound" />
       </Typography>
      </Stack>
     )}
    </Stack>
   </Stack>
   <Stack direction="row" justifyContent="flex-end">
    <DeleteFirewallConfiguration id={Number(params.id)} />
    <Button onClick={handleStartEdit}>
     <FormattedMessage id="app.edit" />
    </Button>
   </Stack>
  </Stack>
 );
};

export default BaremetalFirewall;
